import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Save, CheckCircle, AlertCircle, Eye, EyeOff, Send } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';

export default function SmtpSettingsPanel({ showHeader = true }) {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({ name: '', brand_color: '#1d4ed8' });
  const [smtpForm, setSmtpForm] = useState({ host: '', port: 587, username: '', password: '', use_tls: false });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  const load = () => {
    setLoading(true);
    API.get('/organizations/my')
      .then(r => {
        setOrg(r.data);
        if (r.data) {
          setForm({ name: r.data.name, brand_color: r.data.brand_color || '#1d4ed8' });
          if (r.data.smtp_settings?.host) {
            setSmtpForm({
              host: r.data.smtp_settings.host || '',
              port: r.data.smtp_settings.port || 587,
              username: r.data.smtp_settings.username || '',
              password: '',
              use_tls: r.data.smtp_settings.use_tls || false,
            });
            setSmtpConfigured(true);
          }
        }
      })
      .catch(() => toast.error('Failed to load email settings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    if (!smtpForm.host) { toast.error('SMTP host is required'); return; }
    if (!smtpForm.username) { toast.error('Username/email is required'); return; }
    if (!smtpConfigured && !smtpForm.password) { toast.error('Password is required'); return; }
    setSavingSmtp(true);
    try {
      let currentOrg = org;
      if (!currentOrg) {
        const orgName = form.name?.trim() || `${user?.name || 'My'} Organization`;
        const r = await API.post('/organizations', { name: orgName, brand_color: form.brand_color || '#1d4ed8' });
        currentOrg = r.data;
        setOrg(currentOrg);
      }
      await API.put(`/organizations/${currentOrg.id}`, {
        name: currentOrg.name,
        brand_color: currentOrg.brand_color || '#1d4ed8',
        smtp_settings: { ...smtpForm, port: parseInt(smtpForm.port) || 587 },
      });
      toast.success('SMTP settings saved');
      setSmtpConfigured(true);
      setSmtpForm(p => ({ ...p, password: '' }));
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!org) { toast.error('Save SMTP settings first'); return; }
    setTestingSmtp(true);
    try {
      const r = await API.post(`/organizations/${org.id}/test-smtp`);
      toast.success(r.data.message);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setTestingSmtp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      {showHeader && (
        <>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Mail size={15} className="text-slate-500" /> Email (SMTP)
            </h2>
            {smtpConfigured ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-sm">
                <CheckCircle size={11} /> Configured
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-sm">
                <AlertCircle size={11} /> Not configured
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Configure SMTP to send signing invitations, OTP codes, and completion emails.
            {!org && ' An organization will be created automatically when you save.'}
          </p>
        </>
      )}

      <form onSubmit={handleSaveSmtp} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-medium text-slate-700 mb-1">SMTP Host</label>
            <input
              type="text" value={smtpForm.host}
              onChange={e => setSmtpForm(p => ({ ...p, host: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="smtp.gmail.com"
              data-testid="smtp-host-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Port</label>
            <input
              type="number" value={smtpForm.port}
              onChange={e => setSmtpForm(p => ({ ...p, port: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="587"
              data-testid="smtp-port-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Username / Email</label>
          <input
            type="email" value={smtpForm.username}
            onChange={e => setSmtpForm(p => ({ ...p, username: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="your@gmail.com"
            data-testid="smtp-username-input"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            Password / App Password {smtpConfigured && <span className="text-slate-400 font-normal">(leave blank to keep current)</span>}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'} value={smtpForm.password}
              onChange={e => setSmtpForm(p => ({ ...p, password: e.target.value }))}
              className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={smtpConfigured ? '••••••••••••••••' : 'App password or SMTP password'}
              data-testid="smtp-password-input"
            />
            <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox" id="use_tls" checked={smtpForm.use_tls}
            onChange={e => setSmtpForm(p => ({ ...p, use_tls: e.target.checked, port: e.target.checked ? 465 : 587 }))}
            className="w-4 h-4"
            data-testid="smtp-tls-checkbox"
          />
          <label htmlFor="use_tls" className="text-xs text-slate-700 cursor-pointer">Use TLS/SSL (port 465) — use STARTTLS for port 587</label>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-sm px-4 py-3 text-xs text-blue-700">
          <strong>Google SMTP:</strong> host <code className="bg-blue-100 px-1 rounded">smtp.gmail.com</code>, port <code className="bg-blue-100 px-1 rounded">587</code>, use an{' '}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-medium">App Password</a>.
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit" disabled={savingSmtp}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="save-smtp-btn"
          >
            <Save size={14} />
            {savingSmtp ? 'Saving...' : 'Save SMTP Settings'}
          </button>
          {smtpConfigured && (
            <button
              type="button" onClick={handleTestSmtp} disabled={testingSmtp}
              className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
              data-testid="test-smtp-btn"
            >
              <Send size={14} />
              {testingSmtp ? 'Sending...' : 'Test Connection'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
